import {
  Badge,
  Card,
  Flex,
  Loader,
  Modal,
  Text,
  Title,
  Tooltip,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import React, { useState } from 'react';

import { useDisclosure } from '@mantine/hooks';
import { useGetTariff } from 'api/resource';
import { actionTypes } from 'constants/eobConstants';
import { EOB } from 'interfaces';
import { getEOBActionTypeColor } from 'utils/badgeColors';
import { DataBadge } from './DataBadge';

type DisplayEOB = EOB & {
  tariffID?: string;
  tariffIDs?: string[];
  tariffLabels?: string[];
};

interface EOBCardsCardProps {
  eob: DisplayEOB;
  opened: boolean;
}

export const EOBCard: React.FC<EOBCardsCardProps> = ({ eob, opened }) => {
  const { translate } = useTranslation();
  const hasGroupedTariffs = !!eob.tariffLabels?.length;
  const {
    data: tariff,
    isLoading,
    isError,
  } = useGetTariff({
    id: opened && !hasGroupedTariffs ? eob.tariffID : undefined,
  });

  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      style={{ width: '100%' }}
    >
      {/* Top Section: EOB Code + Description */}
      <Card.Section p="md">
        <Flex align="center" gap="md" wrap="wrap">
          <Badge
            color={getEOBActionTypeColor(eob?.actionType)}
            variant="filled"
          >
            {eob.code}
          </Badge>
          <Title
            order={6}
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              textWrap: 'pretty',
            }}
          >
            {eob.description
              // 1) Lowercase everything
              .toLowerCase()
              // 2) Capitalize any letter at the start of the string or right after ".!? "
              .replace(/(^\s*[a-z]|[.!?]\s*[a-z])/g, (match) =>
                match.toUpperCase()
              )}
          </Title>
        </Flex>
      </Card.Section>

      {/* Bottom Section: Tariff / EOB Action / EOB Description */}
      <Card.Section p="md">
        {hasGroupedTariffs ? (
          <Flex align="baseline" gap="sm" wrap="wrap">
            <Text size="sm" fw={500} c="dimmed">
              {eob.tariffLabels && eob.tariffLabels.length > 1
                ? translate('pages:eobs.labels.tariffs', 'For Tariffs:')
                : translate('pages:eobs.labels.tariff', 'For Tariff:')}
            </Text>
            {eob.tariffLabels?.map((tariffLabel) => (
              <DataBadge key={`${eob.code}-${tariffLabel}`}>
                {tariffLabel}
              </DataBadge>
            ))}
          </Flex>
        ) : isLoading ? (
          <Loader size="sm" />
        ) : isError ? (
          <Text c="red">
            {translate(
              'pages:eobs.messages.errorLoadingTariffData',
              'Error loading tariff data.'
            )}
          </Text>
        ) : (
          tariff && (
            <Text size="sm" fw={500} c="dimmed">
              {translate('pages:eobs.labels.tariff', 'For Tariff:')}{' '}
              <DataBadge>{tariff.code}</DataBadge>
            </Text>
          )
        )}

        <Flex align="baseline" gap="md" wrap="wrap">
          <Text size="sm" fw={500} c="dimmed">
            {translate('pages:eobs.labels.actionType', 'Action Type')}:
          </Text>
          <Badge color="gray" variant="filled">
            {eob.actionType}
          </Badge>
          <Text size="sm" mt="md" fw={500} c="dimmed">
            {actionTypes[eob?.actionType as keyof typeof actionTypes] ?? ''}
          </Text>
        </Flex>
        <Text
          fw={500}
          size="sm"
          mt="md"
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {eob.actionDescription}
        </Text>
      </Card.Section>
    </Card>
  );
};

interface EOBCardsStackProps {
  eobs: DisplayEOB[];
}

export const EOBStack: React.FC<EOBCardsStackProps> = ({ eobs }) => {
  const { translate } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedEOB, setSelectedEOB] = useState<DisplayEOB | null>(null);

  const computedColorScheme = useComputedColorScheme();
  const theme = useMantineTheme();
  return (
    <>
      {eobs?.map((eob) => (
        <React.Fragment key={eob.id}>
          <Tooltip
            label={translate(
              'pages:eobs.messages.clickForDetails',
              'Click for EOB Details'
            )}
          >
            <Badge
              color={theme.colors.red[6]}
              size="md"
              radius="sm"
              style={{
                cursor: 'pointer',
                color:
                  computedColorScheme === 'dark'
                    ? theme.colors.dark[9]
                    : 'white',
              }}
              m={'xs'}
              onClick={() => {
                setSelectedEOB(eob);
                open();
              }}
            >
              {eob.code}
            </Badge>
          </Tooltip>
          <Modal
            opened={opened}
            onClose={() => {
              close();
              setSelectedEOB(null);
            }}
            title={translate(
              'pages:eobs.messages.explanationOfBenefits',
              'Explanation of Benefits'
            )}
            size="lg"
            centered
          >
            {selectedEOB && (
              <EOBCard
                key={selectedEOB?.id}
                eob={selectedEOB}
                opened={opened}
              />
            )}
          </Modal>
        </React.Fragment>
      ))}
    </>
  );
};
